import { useCallback, useEffect, useRef, useState } from 'react';

// ── Window type declarations ──────────────────────────────────────────────────

declare global {
  interface Window {
    /** reCAPTCHA Enterprise API (loaded via enterprise.js?render=SITE_KEY) */
    grecaptcha?: {
      enterprise: {
        ready: (cb: () => void) => void;
        execute: (siteKey: string, options: { action: string }) => Promise<string>;
        render: (
          container: HTMLElement,
          options: {
            sitekey: string;
            callback: (token: string) => void;
            'expired-callback'?: () => void;
            'error-callback'?: () => void;
          },
        ) => number;
        reset: (widgetId: number) => void;
        getResponse: (widgetId: number) => string;
      };
    };
  }
}

interface UseRecaptchaReturn {
  /** Whether the reCAPTCHA v3 script is loaded and ready */
  isReady: boolean;
  /** Execute reCAPTCHA v3 for a given action, returns token or null on failure */
  execute: (action: string) => Promise<string | null>;
  /** Lazily render v2 checkbox widget into the given container (script already loaded by v3) */
  renderV2Widget: (container: HTMLElement) => Promise<void>;
  /** Register callback for when user solves the v2 puzzle */
  onV2Solved: (callback: (token: string) => void) => void;
  /** Reset the v2 widget (for retry after expiry) */
  resetV2: () => void;
  /** Reset internal state (v3 is stateless, kept for API compatibility) */
  reset: () => void;
}

// ── Shared Promise singleton ────────────────────────────────────────────────
// First caller creates the promise, concurrent callers share it.
// On error the cache is nulled so the next call retries.

let v3LoadPromise: Promise<void> | null = null;

/**
 * Upper bound on the enterprise.js load + grecaptcha.ready() handshake.
 *
 * Without it this promise can never settle: `script.onerror` fires for a refused or
 * failed connection, but NOT for a request that simply never answers — an unreachable
 * or throttled www.google.com (captive portals, corporate proxies, regions that filter
 * Google) leaves the caller awaiting forever. Every caller of this module sits on an
 * auth submit, so "forever" is a spinner the user can only escape by leaving the page,
 * and in signup that abandonment is what leaves a Firebase identity behind (#2181).
 *
 * 10s is deliberately generous — the script is ~332 KB, and a slow-but-working
 * connection must not be pushed onto the v2 puzzle. On timeout `execute()` degrades to
 * a null token exactly as it does for a load error, and the server decides what a
 * tokenless request means.
 */
const V3_SCRIPT_LOAD_TIMEOUT_MS = 10_000;

// ── v3 script loader (reCAPTCHA Enterprise) ──────────────────────────────────
// Uses enterprise.js — all API calls go through grecaptcha.enterprise.*
// enterprise.js?render=KEY provides both v3 execute() and v2 render() from a single script.

function loadV3Script(siteKey: string): Promise<void> {
  if (v3LoadPromise) return v3LoadPromise;

  v3LoadPromise = new Promise<void>((resolve, reject) => {
    // Bounded: see V3_SCRIPT_LOAD_TIMEOUT_MS. `settled` keeps a late onload/onerror
    // from touching a promise the timeout already rejected.
    let settled = false;
    const timer = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      v3LoadPromise = null; // allow retry — the script may still arrive later
      reject(new Error('reCAPTCHA Enterprise script load timed out'));
    }, V3_SCRIPT_LOAD_TIMEOUT_MS);

    const succeed = () => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      resolve();
    };
    const fail = (err: Error) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      v3LoadPromise = null; // allow retry
      reject(err);
    };

    // Already loaded — just wait for .ready()
    if (window.grecaptcha?.enterprise?.execute) {
      window.grecaptcha.enterprise.ready(() => succeed());
      return;
    }

    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      window.grecaptcha?.enterprise?.ready(() => succeed());
    };
    script.onerror = () => {
      fail(new Error('Failed to load reCAPTCHA Enterprise script'));
    };
    document.head.appendChild(script);
  });

  return v3LoadPromise;
}

// ── Hook ──────────────────────────────────────────────────────────────────────

/**
 * Shared reCAPTCHA hook — dual v3 (invisible scoring) + v2 (image challenge fallback).
 *
 * Uses reCAPTCHA Enterprise (enterprise.js) — all API calls go through grecaptcha.enterprise.*
 *
 * Primary flow (v3):
 *   - Loads enterprise.js on-demand on first execute() (VITE_RECAPTCHA_V3_SITE_KEY);
 *     pass { prefetchOnIdle: true } on auth surfaces to warm it during idle instead
 *   - execute(action) returns invisible token silently
 *
 * Challenge flow (v2):
 *   - When backend returns RECAPTCHA_CHALLENGE_REQUIRED, call renderV2Widget()
 *   - Renders v2 checkbox into provided container (same enterprise.js script, no extra load)
 *   - onV2Solved() fires when user solves the image puzzle
 *   - Uses existing VITE_RECAPTCHA_SITE_KEY (v2 key)
 *
 * Graceful degradation:
 *   - No VITE_RECAPTCHA_V3_SITE_KEY → execute() returns null
 *   - No VITE_RECAPTCHA_SITE_KEY → renderV2Widget() skips silently
 */
export const RECAPTCHA_DISABLED = import.meta.env.VITE_RECAPTCHA_DISABLE === 'true';

interface UseRecaptchaOptions {
  /**
   * Warm enterprise.js during browser idle so the first execute() has no network
   * wait. Scope this to auth surfaces (login / signup / MFA) ONLY — everywhere
   * else the script loads on-demand in execute(), keeping the ~332 KB off the
   * landing critical path (#1987).
   */
  prefetchOnIdle?: boolean;
}

export function useRecaptcha(options: UseRecaptchaOptions = {}): UseRecaptchaReturn {
  const { prefetchOnIdle = false } = options;
  const [isReady, setIsReady] = useState(RECAPTCHA_DISABLED);
  const v3SiteKeyRef = useRef(
    RECAPTCHA_DISABLED ? '' : (import.meta.env.VITE_RECAPTCHA_V3_SITE_KEY ?? ''),
  );
  const v2SiteKeyRef = useRef(
    RECAPTCHA_DISABLED ? '' : (import.meta.env.VITE_RECAPTCHA_SITE_KEY ?? ''),
  );
  const v2WidgetIdRef = useRef<number | null>(null);
  const v2CallbackRef = useRef<((token: string) => void) | null>(null);

  // ── v3: warm the script on idle (auth surfaces only) ─────────────────────
  // enterprise.js is only needed to score a submit, not to render the form.
  // Loading it eagerly on every consumer's mount put ~332 KB on the landing
  // critical path (#1987). It now loads on-demand in execute(); opt-in callers
  // prefetch during idle so the first login/signup submit still has no wait.
  useEffect(() => {
    if (RECAPTCHA_DISABLED || !v3SiteKeyRef.current) {
      setIsReady(true);
      return;
    }
    if (!prefetchOnIdle) return;

    const warm = () => {
      loadV3Script(v3SiteKeyRef.current)
        .then(() => setIsReady(true))
        .catch(() => setIsReady(true));
    };

    // requestIdleCallback keeps the fetch off first paint; Safari lacks it, so
    // fall back to a post-paint timeout.
    if (typeof window.requestIdleCallback === 'function') {
      const handle = window.requestIdleCallback(warm);
      return () => window.cancelIdleCallback?.(handle);
    }
    const timer = window.setTimeout(warm, 1500);
    return () => window.clearTimeout(timer);
  }, [prefetchOnIdle]);

  // ── v3: execute (invisible scoring) ──────────────────────────────────────
  const execute = useCallback(async (action: string): Promise<string | null> => {
    if (RECAPTCHA_DISABLED) return null;

    const siteKey = v3SiteKeyRef.current;
    if (!siteKey) {
      return null;
    }

    try {
      // Load enterprise.js on-demand — no-op if already loaded (prefetched on
      // auth pages, or by a prior execute()/renderV2Widget() call).
      await loadV3Script(siteKey);
      setIsReady(true);

      const token = await new Promise<string>((resolve, reject) => {
        if (!window.grecaptcha?.enterprise?.execute) {
          reject(new Error('grecaptcha.enterprise not available'));
          return;
        }
        window.grecaptcha.enterprise.ready(() => {
          window.grecaptcha?.enterprise?.execute(siteKey, { action }).then(resolve).catch(reject);
        });
      });
      return token;
    } catch {
      return null;
    }
  }, []);

  // ── v2: render checkbox widget (uses same enterprise.js, no extra script load) ──
  const renderV2Widget = useCallback(async (container: HTMLElement) => {
    if (RECAPTCHA_DISABLED) return;

    const v2SiteKey = v2SiteKeyRef.current;
    if (!v2SiteKey) {
      return;
    }

    try {
      // Ensure enterprise.js is loaded (v3 loads it eagerly, but guard against race)
      if (v3SiteKeyRef.current) {
        await loadV3Script(v3SiteKeyRef.current);
      }

      if (!window.grecaptcha?.enterprise?.render) {
        return;
      }

      v2WidgetIdRef.current = window.grecaptcha.enterprise.render(container, {
        sitekey: v2SiteKey,
        callback: (token: string) => {
          v2CallbackRef.current?.(token);
        },
        'expired-callback': () => {
          if (v2WidgetIdRef.current !== null && window.grecaptcha) {
            window.grecaptcha.enterprise.reset(v2WidgetIdRef.current);
          }
        },
        'error-callback': () => {
          // Widget error — no action needed, user can retry
        },
      });
    } catch {
      // Widget render can fail in edge cases — safe to ignore
    }
  }, []);

  // ── v2: register solved callback ─────────────────────────────────────────
  const onV2Solved = useCallback((callback: (token: string) => void) => {
    v2CallbackRef.current = callback;
  }, []);

  // ── v2: reset widget ────────────────────────────────────────────────────
  const resetV2 = useCallback(() => {
    if (v2WidgetIdRef.current !== null && window.grecaptcha?.enterprise) {
      window.grecaptcha.enterprise.reset(v2WidgetIdRef.current);
    }
  }, []);

  // ── v3: reset (no-op, kept for API compatibility) ────────────────────────
  const reset = useCallback(() => {
    // v3 tokens are single-use and obtained on-demand, so reset is a no-op.
  }, []);

  return { isReady, execute, renderV2Widget, onV2Solved, resetV2, reset };
}
